import React, { useState, useEffect, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Badge, 
  Modal, 
  Alert, 
  Spinner, 
  Toast, 
  ToastContainer,
  ListGroup,Table
} from "react-bootstrap";
import axios from "axios";
import {
  Person,
  ExclamationCircle,
  Shield,
  Key,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Pencil,
  Save,
  X,
  Building,
  CurrencyDollar,
  InfoCircle,
  Copy,
  People,
  Eye,
  FileText
} from "react-bootstrap-icons";

// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function AgentDetails() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Récupérer l'ID de l'utilisateur de manière sécurisée
  const userId = user?.user_id;
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // États principaux
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // États pour les modals
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showTaxpayersModal, setShowTaxpayersModal] = useState(false);
  
  // États pour les données supplémentaires
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [taxpayers, setTaxpayers] = useState([]);
  const [loadingTaxpayers, setLoadingTaxpayers] = useState(false);
  
  // États pour l'expansion des groupes
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // États pour les formulaires
  const [editMode, setEditMode] = useState(false);
  const [agentData, setAgentData] = useState({
    admin_lname: '',
    admin_fname: '',
    email: '',
    admin_phone: '',
    admin_fonction: '',
    admin_email: '',
    is_active: 1
  });
  
  // États pour les notifications
  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  const getAuthToken = () => sessionStorage.getItem("authToken");

  const getHeaders = useCallback(() => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const hideAlert = useCallback(() => {
    setAlertMessage({ show: false, message: '', type: 'info' });
  }, []);

  const showAlert = useCallback((message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => hideAlert(), duration);
    }
  }, [hideAlert]);

  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const notification = {
      id,
      message,
      type,
      show: true
    };
    setNotifications(prev => [notification, ...prev]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, duration);
  };

  const getScopeBadge = (scope) => {
    const colors = { global: "danger", local: "primary", super_admin: "dark" };
    return colors[scope] || "secondary";
  };

  const formatGroupName = (group) => {
    const groups = {
      general: "Général", entite: "Entités", tax: "Taxes", category: "Catégories",
      user: "Utilisateurs", usager: "Usagers", taxpayer: "Contribuables",
      reports: "Rapports", role_permission: "Rôles & Permissions", tax_collector: "Collecte de taxes"
    };
    return groups[group] || group.charAt(0).toUpperCase() + group.slice(1);
  };

  // Charger les permissions de l'utilisateur
  const fetchPermissions = useCallback(async () => {
    try {
      setLoadingPermissions(true);
      const response = await axios.get(`${API_BASE_URL}/users/${userId}/permissions`, { headers: getHeaders() });
      
      let permissionsList = [];
      if (response.data?.permissions && Array.isArray(response.data.permissions)) {
        permissionsList = response.data.permissions;
      } else if (Array.isArray(response.data)) {
        permissionsList = response.data;
      }
      
      const formattedPermissions = permissionsList.map(p => ({
        id: p.permission_id || p.id,
        name: p.name,
        display_name: p.name?.replace(/:/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '',
        description: p.description || '',
        group: p.name?.split(':')[0] || 'general',
        action: p.name?.split(':')[1] || 'access',
        scope: p.scope || 'local'
      }));
      
      setPermissions(formattedPermissions);
      
      const grouped = formattedPermissions.reduce((acc, perm) => {
        const group = perm.group || 'general';
        if (!acc[group]) acc[group] = [];
        acc[group].push(perm);
        return acc;
      }, {});
      
      setGroupedPermissions(grouped);
      setExpandedGroups(Object.keys(grouped).reduce((acc, g) => ({ ...acc, [g]: true }), {}));
      
    } catch (error) {
      console.error('Erreur fetchPermissions:', error);
    } finally {
      setLoadingPermissions(false);
    }
  }, [userId, getHeaders]);

  // Charger les contribuables créés par l'agent
  const fetchTaxpayers = useCallback(async () => {
    try {
      setLoadingTaxpayers(true);
      const response = await axios.get(`${API_BASE_URL}/taxpayers`, { headers: getHeaders() });
      
      let allTaxpayers = [];
      if (response.data?.data && Array.isArray(response.data.data)) allTaxpayers = response.data.data;
      else if (Array.isArray(response.data)) allTaxpayers = response.data;
      
      const filtered = allTaxpayers.filter(t => (t.apiData?.created_by || t.created_by) && parseInt(t.apiData?.created_by || t.created_by) === userId);
      
      setTaxpayers(filtered.map(t => ({
        id: t.id,
        contribuable_id: t.contribuable_id || t.id,
        nom_contribuable: t.nom || t.nom_contribuable || 'Non spécifié',
        email: t.email || 'Non spécifié',
        telephone: t.telephone || 'Non spécifié',
        created_at: t.apiData?.created_at || t.created_at,
        status: t.apiData?.statut || t.statut || 'inactif'
      })));
    } catch (error) {
      console.error('Erreur chargement contribuables:', error);
    } finally {
      setLoadingTaxpayers(false);
    }
  }, [userId, getHeaders]);

  const fetchAgentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/me`, {
        headers: getHeaders()
      });
      if (response.data && response.data.user) {
        const userData = response.data.user;
        setAgent(userData);
        setAgentData({
          admin_lname: userData.admin_lname || userData.full_name?.split(' ')[0] || '',
          admin_fname: userData.admin_fname || userData.full_name?.split(' ').slice(1).join(' ') || '',
          email: userData.email || '',
          admin_phone: userData.admin_phone || userData.telephone || '',
          admin_fonction: userData.admin_fonction || userData.fonction || '',
          admin_email: userData.admin_email || '',
          is_active: userData.is_active || 1
        });
      } else {
        throw new Error('Format de données invalide');
      }
    } catch (error) {
      setError(`Erreur de chargement: ${error.message}`);
      showAlert(`Erreur de chargement: ${error.message}`, 'danger');
    } finally {
      setLoading(false);
    }
  }, [userId, getHeaders, showAlert]);

  // Handlers
  const handleShowPermissions = () => { fetchPermissions(); setShowPermissionsModal(true); };
  const handleShowTaxpayers = () => { fetchTaxpayers(); setShowTaxpayersModal(true); };

  const toggleGroup = (group) => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));

  // Initialiser les données
  useEffect(() => {
    if (userId) {
      fetchAgentDetails();
    }
  }, [fetchAgentDetails, userId]);

  // Redirection si non authentifié
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user)) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fonctions de formatage
  const formatDate = (dateString) => {
    if (!dateString) return "Non spécifié";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR') + ' ' + date.toLocaleTimeString('fr-FR');
  };

  const formatDateSimple = (dateString) => {
    if (!dateString) return "Non spécifié";
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Obtenir le badge de rôle
  const getRoleBadge = (roleName) => {
    const roleColors = {
      super_admin: "danger",
      admin: "primary",
      iam: "info",
      comptable: "warning",
      developper: "dark",
      lecture_seule: "secondary",
      reporter: "success",
      super_admin_iam: "danger",
      super_admin_compta: "danger",
      super_admin_dev: "danger",
      super_admin_reporter: "danger",
      taxpayer_admin: "info",
      tax_collector: "success"
    };
    
    const color = roleColors[roleName] || "secondary";
    const label = roleName ? roleName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Non défini";
    
    return (
      <Badge bg={color} className="d-flex align-items-center px-2 py-1">
        <Shield className="me-1" />
        {label}
      </Badge>
    );
  };

  // Obtenir le badge de statut
  const getStatusBadge = (isActive) => {
    const status = isActive === 1 ? "actif" : "inactif";
    const config = {
      actif: { label: "Actif", color: "success", icon: <CheckCircle className="me-1" /> },
      inactif: { label: "Inactif", color: "secondary", icon: <XCircle className="me-1" /> }
    };
    const cfg = config[status] || { label: status, color: "secondary", icon: null };
    return (
      <Badge bg={cfg.color} className="d-flex align-items-center px-2 py-1">
        {cfg.icon}
        {cfg.label}
      </Badge>
    );
  };

  // Gestion de l'édition
  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  // Sauvegarder les modifications
  const handleSaveChanges = async () => {
    if (!agentData.email.trim() || !agentData.admin_lname.trim() || !agentData.admin_fname.trim()) {
      showAlert("Veuillez remplir les champs obligatoires (email, nom et prénom)", 'warning');
      return;
    }

    setSaving(true);

    try {
      const updateData = {
        email: agentData.email.trim(),
        full_name: `${agentData.admin_lname.trim()} ${agentData.admin_fname.trim()}`,
        first_name: agentData.admin_fname.trim(),
        telephone: agentData.admin_phone.trim(),
        fonction: agentData.admin_fonction.trim(),
        admin_email: agentData.admin_email.trim()
      };

      const response = await axios.put(
        `${API_BASE_URL}/me`,
        updateData,
        { headers: getHeaders() }
      );

      if (response.data && response.data.user) {
        setEditMode(false);
        await fetchAgentDetails();
        showNotification('Modifications enregistrées avec succès', 'success');
      } else {
        showNotification('Modifications enregistrées', 'success');
        setEditMode(false);
      }
    } catch (error) {
      let errorMessage = "Erreur lors de la mise à jour";
      
      if (error.response?.status === 422) {
        errorMessage = "Erreur de validation des données";
        if (error.response?.data?.errors) {
          const errors = Object.values(error.response.data.errors).flat();
          errorMessage = `Erreurs: ${errors.join(', ')}`;
        }
      } else if (error.response?.status === 404) {
        errorMessage = "Agent non trouvé";
      } else if (error.response?.status === 500) {
        errorMessage = "Erreur serveur interne";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showAlert(errorMessage, 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser le mot de passe
  const handleResetPassword = async () => {
    try {
      setResettingPassword(true);
      
      const response = await axios.post(
        `${API_BASE_URL}/users/${userId}/reset-password`,
        { user_id: userId },
        { headers: getHeaders() }
      );

      if (response.data && response.data.password_show) {
        setResetPasswordResult(response.data);
        setShowResetPasswordModal(false);
        setShowPasswordModal(true);
        showNotification('Mot de passe réinitialisé avec succès', 'success');
      }
    } catch (error) {
      console.error('Erreur handleResetPassword:', error);
      showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setResettingPassword(false);
    }
  };

  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Chargement de l'authentification...</span>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

   if (loading) {
   return (
     <>
       <Header />
       <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
         <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
           <div className="text-center">
             <div className="position-relative d-inline-block">
               <Spinner 
                 animation="border" 
                 variant="primary" 
                 style={{ width: '4rem', height: '4rem' }}
               />
               <div 
                 className="position-absolute top-50 start-50 translate-middle"
                 style={{ width: '2rem', height: '2rem' }}
               >
                 <div className="spinner-grow text-primary" style={{ width: '1rem', height: '1rem' }} />
               </div>
             </div>
             <h5 className="mt-4 fw-semibold text-primary">Chargement en cours...</h5>
             <p className="text-muted small mt-2">Veuillez patienter pendant que nous chargeons vos données</p>
             <div className="progress mt-3" style={{ width: '250px', height: '4px', margin: '0 auto' }}>
               <div 
                 className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                 style={{ width: '100%' }}
               />
             </div>
           </div>
         </div>
       </div>
       <Footer />
     </>
   );
 }

  const displayFullName = `${agent?.admin_lname || ''} ${agent?.admin_fname || ''}`.trim() || agent?.full_name || 'Non spécifié';

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
          {notifications.map((notification) => (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
              delay={8000}
              autohide
              className={`bg-${notification.type === 'success' ? 'success' : 
                         notification.type === 'error' ? 'danger' :
                         notification.type === 'warning' ? 'warning' : 'info'} text-white`}
            >
              <Toast.Header>
                {notification.type === 'success' ? <CheckCircle className="me-2" /> :
                 notification.type === 'error' ? <XCircle className="me-2" /> :
                 notification.type === 'warning' ? <ExclamationCircle className="me-2" /> :
                 <CheckCircle className="me-2" />}
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' : 
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
              </Toast.Header>
              <Toast.Body>{notification.message}</Toast.Body>
            </Toast>
          ))}
        </ToastContainer>

        {alertMessage.show && (
          <div className="container-fluid mb-3">
            <Alert variant={alertMessage.type} dismissible onClose={hideAlert} className="shadow-sm border-0">
              <div className="d-flex align-items-center">
                {alertMessage.type === 'success' ? <CheckCircle className="me-2 fs-5" /> :
                 alertMessage.type === 'danger' ? <XCircle className="me-2 fs-5" /> :
                 alertMessage.type === 'warning' ? <ExclamationCircle className="me-2 fs-5" /> :
                 <CheckCircle className="me-2 fs-5" />}
                <span className="fw-medium">{alertMessage.message}</span>
              </div>
            </Alert>
          </div>
        )}

        <div className="container-fluid">
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Mon profil</li>
                  </ol>
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <h4 className="main-title mb-0">{displayFullName}</h4>
                    {agent && getStatusBadge(agent.is_active)}
                    {getRoleBadge(agent.role?.name)}
                  </div>
                  <p className="text-muted mb-0 mt-1">
                    ID: {agent?.user_id} • Email: {agent?.email}
                  </p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-secondary" onClick={() => navigate('/dashboard')}>
                    <ArrowLeft className="me-2" /> Retour
                  </Button>
                  <Button variant="outline-primary" onClick={handleEditToggle}>
                    <Pencil className="me-2" /> {editMode ? 'Annuler' : 'Modifier'}
                  </Button>
                  <Button variant="outline-info" onClick={() => setShowResetPasswordModal(true)} disabled={resettingPassword}>
                    {resettingPassword ? <Spinner size="sm" className="me-2" /> : <Key className="me-2" />}
                    Réinitialiser MDP
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="g-4">
            {/* Colonne gauche - Informations de base */}
            <Col lg={8}>
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                  <Card.Title className="mb-0">
                    <Person className="me-2" /> Mes Informations
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {editMode ? (
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Nom de famille <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={agentData.admin_lname}
                            onChange={(e) => setAgentData({...agentData, admin_lname: e.target.value})}
                            placeholder="Votre nom"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Prénom <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={agentData.admin_fname}
                            onChange={(e) => setAgentData({...agentData, admin_fname: e.target.value})}
                            placeholder="Votre prénom"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="email"
                            value={agentData.email}
                            onChange={(e) => setAgentData({...agentData, email: e.target.value})}
                            placeholder="exemple@email.com"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Téléphone</Form.Label>
                          <Form.Control
                            type="tel"
                            value={agentData.admin_phone}
                            onChange={(e) => setAgentData({...agentData, admin_phone: e.target.value})}
                            placeholder="+241 77 123 45 67"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Fonction/Rôle</Form.Label>
                          <Form.Control
                            type="text"
                            value={agentData.admin_fonction}
                            onChange={(e) => setAgentData({...agentData, admin_fonction: e.target.value})}
                            placeholder="Ex: Agent fiscal, Superviseur, etc."
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <Button variant="secondary" onClick={() => { setEditMode(false); fetchAgentDetails(); }}>
                            <X className="me-2" /> Annuler
                          </Button>
                          <Button variant="primary" onClick={handleSaveChanges} disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : <Save className="me-2" />}
                            Enregistrer
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col md={6}>
                        <div className="mb-4">
                          <h6 className="text-muted mb-2">Identité</h6>
                          <p className="mb-1"><strong>Nom complet:</strong> {displayFullName}</p>
                          <p className="mb-1"><strong>Email:</strong> {agent?.email}</p>
                          <p className="mb-1"><strong>Téléphone:</strong> {agent?.admin_phone || agent?.telephone || 'Non spécifié'}</p>
                          <p className="mb-1"><strong>Fonction:</strong> {agent?.admin_fonction || agent?.fonction || 'Non spécifié'}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <h6 className="text-muted mb-2">Statut du compte</h6>
                          <p className="mb-1"><strong>Statut:</strong> {getStatusBadge(agent?.is_active)}</p>
                          <p className="mb-1"><strong>Type de compte:</strong> {agent?.type_compte || 'Standard'}</p>
                          <p className="mb-1"><strong>Dernière connexion:</strong> {agent?.last_login_at ? formatDate(agent.last_login_at) : "Jamais"}</p>
                          <p className="mb-1"><strong>Créé le:</strong> {formatDate(agent?.created_at)}</p>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Colonne droite - Actions et Métadonnées */}
            <Col lg={4}>
              {/* Actions Rapides */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-info text-white py-3">
                  <Card.Title className="mb-0"><Key className="me-2" />Actions Rapides</Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <ListGroup variant="flush">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center px-4 py-3">
                      <div><Shield className="me-2 text-primary" />Voir mes permissions</div>
                      <Button size="sm" variant="outline-primary" onClick={handleShowPermissions}>
                        <Eye className="me-1" />Voir
                      </Button>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center px-4 py-3">
                      <div><People className="me-2 text-success" />Mes contribuables {taxpayers.length > 0 && <Badge bg="success" className="ms-2">{taxpayers.length}</Badge>}</div>
                      <Button size="sm" variant="outline-success" onClick={handleShowTaxpayers}>
                        <Eye className="me-1" />Voir
                      </Button>
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>

              {/* Métadonnées Système */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-secondary text-white py-3">
                  <Card.Title className="mb-0"><Building className="me-2" />Métadonnées Système</Card.Title>
                </Card.Header>
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">Informations techniques</h6>
                    <p className="mb-1 small"><strong>ID Utilisateur:</strong> {agent?.user_id}</p>
                    <p className="mb-1 small"><strong>ID Base:</strong> {agent?.id || 'N/A'}</p>
                    <p className="mb-1 small"><strong>Rôle ID:</strong> {agent?.role_id}</p>
                    <p className="mb-1 small"><strong>Ajouté par:</strong> {agent?.added_by || "Système"}</p>
                  </div>
                  <div>
                    <h6 className="text-muted mb-2">Dates importantes</h6>
                    <p className="mb-1 small"><strong>Création:</strong> {formatDateSimple(agent?.created_at)}</p>
                    <p className="mb-1 small"><strong>Modification:</strong> {formatDateSimple(agent?.updated_at)}</p>
                  </div>
                </Card.Body>
              </Card>

              {/* Identifiants Internes */}
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-success text-white py-3">
                  <Card.Title className="mb-0"><Building className="me-2" /><Key className="me-2" size={16} /> Identifiants Internes</Card.Title>
                </Card.Header>
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">Informations internes</h6>
                    <p className="mb-1 small"><strong>Matricule interne:</strong> {agent?.matricule_interne || "Non spécifié"}</p>
                    <p className="mb-1 small"><strong>Code agent interne:</strong> {agent?.code_agent_interne || "Non spécifié"}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Modal des permissions */}
        <Modal show={showPermissionsModal} onHide={() => setShowPermissionsModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title><Shield className="me-2" />Mes Permissions ({permissions.length})</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {loadingPermissions ? (
              <div className="d-flex flex-column justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                <p className="mt-3 text-muted">Chargement des permissions...</p>
              </div>
            ) : permissions.length === 0 ? (
              <div className="text-center py-5">
                <Shield size={48} className="text-muted mb-3 opacity-50" />
                <p className="text-muted">Aucune permission associée à votre compte.</p>
              </div>
            ) : (
              Object.entries(groupedPermissions).map(([group, perms]) => (
                <Card key={group} className="mb-3 border shadow-sm">
                  <Card.Header 
                    className="bg-light py-3 d-flex justify-content-between align-items-center" 
                    style={{ cursor: 'pointer' }} 
                    onClick={() => toggleGroup(group)}
                  >
                    <div className="d-flex align-items-center">
                      <strong className="text-dark">{formatGroupName(group)}</strong>
                      <Badge bg="primary" className="ms-2 rounded-pill">{perms.length}</Badge>
                    </div>
                  </Card.Header>
                  {expandedGroups[group] && (
                    <Card.Body className="p-3">
                      <Row className="g-3">
                        {perms.map(p => (
                          <Col md={6} lg={4} key={p.id}>
                            <Card className="border h-100 shadow-sm">
                              <Card.Body className="p-3">
                                <div className="d-flex align-items-start mb-2">
                                  <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-2">
                                    <Shield className="text-primary" size={16} />
                                  </div>
                                  <div className="flex-grow-1">
                                    <h6 className="fw-semibold mb-1">{p.display_name}</h6>
                                    {p.description && <p className="text-muted small mb-2">{p.description}</p>}
                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                      <Badge bg="info" className="rounded-pill px-2 py-1"><small>{p.action}</small></Badge>
                                      <Badge bg={getScopeBadge(p.scope)} className="rounded-pill px-2 py-1">
                                        <small>{p.scope === 'global' ? '🌍 Global' : p.scope === 'local' ? '🏢 Local' : p.scope}</small>
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  )}
                </Card>
              ))
            )}
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowPermissionsModal(false)}>Fermer</Button></Modal.Footer>
        </Modal>

        {/* Modal des contribuables */}
        <Modal show={showTaxpayersModal} onHide={() => setShowTaxpayersModal(false)} size="xl" centered>
          <Modal.Header closeButton className="bg-success text-white border-0">
            <Modal.Title className="d-flex align-items-center gap-2">
              <People size={22} />
              <span>Mes Contribuables</span>
              <Badge bg="light" text="dark" className="ms-2 rounded-pill">{taxpayers.length}</Badge>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>ID</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Date création</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTaxpayers ? (
                    <tr><td colSpan="5" className="text-center py-5"><Spinner animation="border" variant="success" /></td></tr>
                  ) : taxpayers.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-5"><People size={48} className="text-muted mb-3 opacity-50" /><p>Aucun contribuable créé</p></td></tr>
                  ) : (
                    taxpayers.map(t => (
                      <tr key={t.id}>
                        <td><Badge bg="secondary">{t.contribuable_id}</Badge></td>
                        <td><strong>{t.nom_contribuable}</strong></td>
                        <td>{t.email}</td>
                        <td>{t.telephone}</td>
                        <td className="text-muted small">{formatDateSimple(t.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light">
            <Button variant="secondary" onClick={() => setShowTaxpayersModal(false)}>Fermer</Button>
          </Modal.Footer>
        </Modal>

        {/* Modals de réinitialisation de mot de passe */}
        <Modal show={showResetPasswordModal} onHide={() => !resettingPassword && setShowResetPasswordModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title><Key className="text-info me-2" />Réinitialiser le mot de passe</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <Key className="display-4 text-info mb-3" size={48} />
              <h5>Réinitialiser votre mot de passe ?</h5>
              <div className="mt-3 p-3 bg-light rounded">
                <strong>{displayFullName}</strong><br />
                <small className="text-muted">{agent?.email}</small>
              </div>
              <p className="text-muted mt-3">
                Un nouveau mot de passe temporaire sera généré.
                <br />
                <strong>Vous devrez changer ce mot de passe lors de votre prochaine connexion.</strong>
              </p>
              <Alert variant="warning" className="mt-3">
                <ExclamationCircle className="me-2" /> Vous serez déconnecté après cette action.
              </Alert>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)} disabled={resettingPassword}>Annuler</Button>
            <Button variant="info" onClick={handleResetPassword} disabled={resettingPassword}>
              {resettingPassword ? <Spinner size="sm" className="me-2" /> : <Key className="me-2" />}
              Confirmer
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered size="lg">
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title><CheckCircle className="me-2" />Mot de passe réinitialisé</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-4">
            <div className="mb-4">
              <div className="bg-light p-3 rounded-3">
                <small className="text-muted text-uppercase">Utilisateur</small>
                <h5 className="mb-0">{resetPasswordResult?.username || agent?.email}</h5>
              </div>
            </div>
            <div className="mb-4">
              <div className="bg-warning bg-opacity-10 p-4 rounded-3 border border-warning">
                <small className="text-muted text-uppercase">Nouveau mot de passe temporaire</small>
                <div className="d-flex align-items-center justify-content-center gap-3 mt-2 flex-wrap">
                  <h1 className="text-warning fw-bold mb-0" style={{ fontFamily: 'monospace', letterSpacing: '4px' }}>
                    {resetPasswordResult?.password_show}
                  </h1>
                  <Button variant="outline-warning" size="sm" onClick={() => { navigator.clipboard.writeText(resetPasswordResult?.password_show); showNotification('Mot de passe copié', 'success'); }}>
                    <Copy className="me-1" size={14} /> Copier
                  </Button>
                </div>
              </div>
            </div>
            <Alert variant="info" className="mt-2">
              <InfoCircle className="me-2" />
              <strong>Information importante :</strong><br />
              Ce mot de passe est temporaire. Vous devrez le changer lors de votre prochaine connexion.
              <hr className="my-2" />
              <small>ID: {resetPasswordResult?.user_id || agent?.user_id}</small>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>Fermer</Button>
            <Button variant="success" onClick={() => { navigator.clipboard.writeText(resetPasswordResult?.password_show); setShowPasswordModal(false); }}>
              <Copy className="me-2" /> Copier et fermer
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}